import React, { useEffect, useMemo, useState } from "react";
import { API } from "../config/api";
import { get } from "../lib/http";

const cn = (...a) => a.filter(Boolean).join(" ");

export default function ImportantNoticePopup({ hidden = false }) {
  const [loading, setLoading] = useState(false);
  const [notice, setNotice] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    if (hidden) return () => {};

    (async () => {
      try {
        setLoading(true);
        const res = await get(API.notice.active);
        if (!alive) return;
        const n = res || null;
        setNotice(n);

        if (!n?._id) {
          setOpen(false);
          return;
        }

        const key = `important_notice_seen_${n._id}`;
        const seen = sessionStorage.getItem(key) === "1";
        setOpen(!seen);
      } catch (err) {
        console.error("important notice load failed:", err);
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [hidden]);

  const dismissKey = useMemo(
    () => (notice?._id ? `important_notice_seen_${notice._id}` : null),
    [notice?._id]
  );

  const close = () => {
    if (dismissKey) sessionStorage.setItem(dismissKey, "1");
    setOpen(false);
  };

  if (hidden || loading || !notice || !open) return null;

  return (
    <div className="fixed inset-0 z-[140] flex items-center justify-center p-3 sm:p-6">
      <button
        type="button"
        className="absolute inset-0 bg-slate-950/65 backdrop-blur-[2px]"
        onClick={close}
        aria-label="Close notice"
      />

      <div className="relative z-[141] w-full max-w-2xl overflow-hidden rounded-3xl border border-amber-200/70 bg-white shadow-[0_28px_80px_-20px_rgba(15,23,42,0.6)] dark:border-amber-700/40 dark:bg-gray-900">
        <div className="pointer-events-none absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-amber-400 via-orange-400 to-amber-500" />

        <div className="flex items-center justify-between border-b border-amber-100 bg-gradient-to-r from-amber-50 via-white to-orange-50 px-4 py-3 dark:border-gray-800 dark:from-gray-900 dark:via-gray-900 dark:to-gray-900 sm:px-5">
          <h2 className="truncate text-base font-extrabold tracking-tight text-slate-900 dark:text-white sm:text-lg">
            {notice.title || "Important Notice"}
          </h2>
          <button
            type="button"
            onClick={close}
            className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 hover:border-amber-300 hover:bg-amber-50 dark:border-gray-700 dark:bg-gray-900 dark:text-slate-300 dark:hover:bg-gray-800"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <div className="max-h-[78vh] overflow-y-auto">
          {notice.imageUrl ? (
            <div className="border-b border-amber-100 bg-gradient-to-b from-amber-50/70 to-white p-3 dark:border-gray-800 dark:from-gray-950 dark:to-gray-950 sm:p-4">
              <img
                src={notice.imageUrl}
                alt={notice.title || "Notice"}
                className="max-h-[42vh] w-full rounded-2xl border border-amber-100 bg-white object-contain shadow-sm dark:border-gray-700 dark:bg-gray-900"
              />
            </div>
          ) : null}

          <div className="bg-white px-4 py-4 dark:bg-gray-900 sm:px-5 sm:py-5">
            <div
              className={cn(
                "prose prose-sm max-w-none leading-relaxed text-slate-700",
                "prose-headings:mb-2 prose-headings:text-slate-900 prose-a:text-amber-700",
                "prose-strong:text-slate-900",
                "dark:prose-invert dark:text-slate-200"
              )}
              dangerouslySetInnerHTML={{ __html: notice.contentHtml || "" }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
